import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Scissors,
  Layers,
  ArrowDown,
  ArrowRight,
  Package,
  Printer,
  Building2,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatNumber } from '@/lib/utils';

interface PaperSlitConfirmModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: any;
  onSuccess?: (result: any) => void;
}

export function PaperSlitConfirmModal({
  open,
  onOpenChange,
  item,
  onSuccess,
}: PaperSlitConfirmModalProps) {
  const queryClient = useQueryClient();

  const [slittedSheets, setSlittedSheets] = useState<number>(0);
  const [grainDirection, setGrainDirection] = useState<'LONG_GRAIN' | 'SHORT_GRAIN'>('LONG_GRAIN');
  const [palletCount, setPalletCount] = useState<number>(1);
  const [operatorName, setOperatorName] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [autoIssueInventory, setAutoIssueInventory] = useState<boolean>(true);

  // Initialize values when item changes
  useEffect(() => {
    if (item) {
      const defaultSheets = item.slittedSheets > 0
        ? item.slittedSheets
        : (item.requiredSheets || 0) + (item.bufferSheets || 0);
      setSlittedSheets(defaultSheets || 1000);
      setGrainDirection(item.grainDirection || 'LONG_GRAIN');
      const calculatedPallets = Math.max(1, Math.ceil((defaultSheets || 1000) / 2500));
      setPalletCount(item.palletCount || calculatedPallets);
      setOperatorName(item.slittedBy || '');
      setNotes(`Xác nhận xả mộc cho LSX ${item.lsx || item.poOrderId}`);
      setAutoIssueInventory(true);
    }
  }, [item]);

  const confirmMutation = useMutation(
    trpc.productionPlanning.confirmPaperSlittingAndIssue.mutationOptions({
      onSuccess: (res: any) => {
        toast.success(res.message || 'Xác nhận xả mộc và xuất kho thành công!');
        queryClient.invalidateQueries({ queryKey: [['productionPlanning', 'getPaperAllocationAndSurplus']] });
        queryClient.invalidateQueries({ queryKey: [['productionOrders']] });
        onOpenChange(false);
        if (onSuccess) {
          onSuccess(res);
        }
      },
      onError: (err: any) => {
        toast.error(`Lỗi xả mộc: ${err.message}`);
      },
    })
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!item?.id && !item?.materialId) {
      toast.error('Thiếu mã vật tư phôi giấy');
      return;
    }
    if (slittedSheets <= 0) {
      toast.error('Số lượng tờ mộc phải lớn hơn 0');
      return;
    }

    confirmMutation.mutate({
      materialId: item.materialId || item.id,
      slittedSheets: Number(slittedSheets),
      grainDirection,
      palletCount: Number(palletCount),
      operatorName: operatorName || undefined,
      notes: notes || undefined,
      autoIssueInventory,
    });
  };

  if (!item) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">
                <Scissors className="h-5 w-5" />
              </div>
              <div>
                <DialogTitle>
                  Xác Nhận Xả Mộc & Xuất Kho Phôi Giấy
                </DialogTitle>
                <DialogDescription className="text-xs text-slate-500">
                  LSX: <strong className="text-slate-800 font-mono">{item.lsx || item.poOrderId}</strong> &bull; Sản phẩm: {item.productName || item.itemCode}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="space-y-4 py-3">
            {/* Quick Specs Card */}
            <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200 grid grid-cols-3 gap-2 text-xs">
              <div>
                <span className="text-slate-500 block">Khổ Mua Ram:</span>
                <span className="font-semibold text-slate-900 font-mono">{item.notes || '790 x 1090 mm'}</span>
              </div>
              <div>
                <span className="text-slate-500 block">Khổ Xả Mộc:</span>
                <span className="font-semibold text-indigo-700 font-mono">
                  {item.notes || '540 x 790 mm'}
                </span>
              </div>
              <div>
                <span className="text-slate-500 block">Tỉ Lệ Cắt Xả:</span>
                <Badge variant="outline" className="font-mono bg-white text-slate-800">
                  Chia {item.layoutUps || 2}
                </Badge>
              </div>
            </div>

            {/* Input: Số tờ mộc xả thực tế */}
            <div className="space-y-1.5">
              <div className="flex justify-between items-center">
                <Label className="text-xs font-bold text-slate-800">
                  Số Tờ In Mộc Thực Tế Sau Xả:
                </Label>
                <span className="text-xs text-slate-500">
                  Định mức: {formatNumber((item.requiredSheets || 0) + (item.bufferSheets || 0))} tờ
                </span>
              </div>
              <Input
                type="number"
                min={1}
                value={slittedSheets}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setSlittedSheets(val);
                  setPalletCount(Math.max(1, Math.ceil(val / 2500)));
                }}
                className="font-mono text-base font-bold text-slate-900"
                required
              />
            </div>

            {/* Selection: Chiều Xớ Giấy (Grain Direction) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-bold text-slate-800">
                Chiều Xớ Giấy In Offset (Bắt buộc theo quy chuẩn INV-04):
              </Label>
              <div className="grid grid-cols-2 gap-3">
                <div
                  onClick={() => setGrainDirection('LONG_GRAIN')}
                  className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    grainDirection === 'LONG_GRAIN'
                      ? 'border-indigo-600 bg-indigo-50/40 shadow-xs'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ArrowDown className="h-4 w-4 text-amber-600" />
                    <span className="font-bold text-xs text-slate-900">XỚ XUÔI (Long Grain)</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-tight">
                    Xớ giấy chạy dọc chiều nhíp in. Khuyên dùng cho in Offset để chống quăn mép.
                  </p>
                </div>

                <div
                  onClick={() => setGrainDirection('SHORT_GRAIN')}
                  className={`p-3 rounded-xl border-2 transition-all cursor-pointer ${
                    grainDirection === 'SHORT_GRAIN'
                      ? 'border-indigo-600 bg-indigo-50/40 shadow-xs'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ArrowRight className="h-4 w-4 text-purple-600" />
                    <span className="font-bold text-xs text-slate-900">XỚ NGANG (Short Grain)</span>
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1 leading-tight">
                    Xớ giấy chạy ngang chiều in. Thường dùng khi bế hộp cần gân gập chịu lực.
                  </p>
                </div>
              </div>
            </div>

            {/* Selection: Số Kiện Pallet Chia Tải */}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-800">Số Lượng Kiện Pallet:</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min={1}
                    max={20}
                    value={palletCount}
                    onChange={(e) => setPalletCount(Number(e.target.value))}
                    className="font-mono font-bold text-sm"
                  />
                  <span className="text-xs text-slate-500 whitespace-nowrap">
                    (~{formatNumber(Math.round(slittedSheets / (palletCount || 1)))} tờ/pallet)
                  </span>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-slate-800">Thợ Cắt Xén Phôi:</Label>
                <Input
                  type="text"
                  placeholder="Ví dụ: Nguyễn Văn A"
                  value={operatorName}
                  onChange={(e) => setOperatorName(e.target.value)}
                  className="text-sm font-medium"
                />
              </div>
            </div>

            {/* Checkbox: Tự Động Xuất Kho NVL */}
            <div className="p-3 bg-emerald-50/60 rounded-xl border border-emerald-200 flex items-start gap-2.5">
              <Checkbox
                id="auto-issue"
                checked={autoIssueInventory}
                onCheckedChange={(checked) => setAutoIssueInventory(!!checked)}
                className="mt-0.5"
              />
              <div className="space-y-0.5">
                <label
                  htmlFor="auto-issue"
                  className="text-xs font-bold text-emerald-950 cursor-pointer"
                >
                  Tự động sinh phiếu xuất kho NVL và chuyển LSX sang trạng thái READY
                </label>
                <p className="text-[11px] text-emerald-700 leading-tight">
                  Tự động trừ số ram giấy tại Kho Vật Tư (KHO-VT) và mở khóa trạm máy in Offset tại xưởng MES.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={confirmMutation.isPending}
            >
              Hủy
            </Button>
            <Button
              type="submit"
              disabled={confirmMutation.isPending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold gap-2 shadow-xs"
            >
              {confirmMutation.isPending ? (
                <>Đang lưu...</>
              ) : (
                <>
                  <CheckCircle2 className="h-4 w-4" />
                  Xác Nhận Xả Mộc & Xuất Kho
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
